import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useEffectEvent, useMemo } from 'react';
import { indexRowRefreshVersionAtom } from '../../state';
import {
  CachedChartResponse,
  InstrumentStore,
  InstrumentSubscriptionDataScope,
  ListedInstrument,
  QuoteChartEntry,
} from '../../types';
import { isDefined, Loadable, LoadableState } from '../../utils';
import { SubscribeToInstrumentChangesFn, useInstrumentStoreItem } from '../sse';
import { useIndexRowChartDataCommon } from './useIndexRowChartDataCommon';

const dataScope: InstrumentSubscriptionDataScope[] = [InstrumentSubscriptionDataScope.MarketData];

export type IndexRowMarketValues = {
  lastKnownPrice?: number;
  changeNominal?: number;
  changePercentage?: number;
};

export type UseIndexRowInstrumentCommonParams = {
  instrument: LoadableState<ListedInstrument>;
  instrumentStore: InstrumentStore;
  subscribeToInstrumentChanges: SubscribeToInstrumentChangesFn;
  getCachedIntraDayChart: (ric: string) => Promise<CachedChartResponse>;
  isPollingEnabled?: boolean;
};

export type UseIndexRowInstrumentCommonOutput = {
  entries: LoadableState<QuoteChartEntry[]>;
  marketValues: LoadableState<IndexRowMarketValues>;
  subscribe: () => void;
  unsubscribe: () => void;
};

export const useIndexRowInstrumentCommon = ({
  instrument,
  instrumentStore,
  subscribeToInstrumentChanges,
  getCachedIntraDayChart,
  isPollingEnabled = true,
}: UseIndexRowInstrumentCommonParams): UseIndexRowInstrumentCommonOutput => {
  const { entries, startPolling, stopPolling } = useIndexRowChartDataCommon({
    instrument,
    getCachedIntraDayChart,
    isEnabled: isPollingEnabled,
  });
  const refreshVersion = useAtomValue(indexRowRefreshVersionAtom);
  const ric = Loadable.hasValue(instrument) ? instrument.ric : undefined;
  const instrumentStoreItem = useInstrumentStoreItem(
    Loadable.hasValue(instrument) ? instrument : null,
    instrumentStore,
    subscribeToInstrumentChanges,
    {
      source: 'indexRow',
      dataScope,
      forceRequestStaticData: true,
    },
  );

  const marketValues = useMemo<LoadableState<IndexRowMarketValues>>(() => {
    if (!isDefined(instrumentStoreItem)) {
      return Loadable.loading;
    }
    const { raw, details } = instrumentStoreItem;

    const lastKnownPrice = raw?.TRDPRC_1 ?? raw?.PRIMACT_1 ?? raw?.HST_CLOSE;
    const changeNominal = details?.changeNominalRaw ?? details?.changeNominalPreviousRaw;
    const changePercentage = details?.changePercentageRaw ?? details?.changePercentagePreviousRaw;

    return Loadable.loaded<IndexRowMarketValues>({
      lastKnownPrice,
      changeNominal,
      changePercentage,
    });
  }, [instrumentStoreItem]);

  const unsubscribe = useCallback((): void => {
    stopPolling();
  }, [stopPolling]);

  const subscribe = useCallback((): void => {
    startPolling();
  }, [startPolling]);

  const handleRefresh = useEffectEvent((): void => {
    stopPolling();
    startPolling();
  });

  useEffect(() => {
    stopPolling();
  }, [ric, stopPolling]);

  useEffect(() => {
    if (!isDefined(refreshVersion)) {
      return;
    }
    handleRefresh();
  }, [refreshVersion]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return useMemo<UseIndexRowInstrumentCommonOutput>(
    () => ({
      entries,
      marketValues,
      subscribe,
      unsubscribe,
    }),
    [entries, marketValues, subscribe, unsubscribe],
  );
};
